import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Layout from '@/components/Layout';
import { api } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import ChordChart from '@/components/ChordChart';
import BackingTrackPreview from '@/components/BackingTrackPreview';
import AudioAnalyzeButton from '@/components/AudioAnalyzeButton';
import { parseChordText } from '@/lib/chordTextParser';
import { estimateDifficulty, DIFFICULTY_LABEL_FR } from '@/lib/difficultyEstimator';
import { Crown, PlusCircle, Music2, Save, Trash2, Eye, FileEdit, Sparkles, ArrowRight, Upload, X, Loader2, Wand2 } from 'lucide-react';

const GENRES = ['rock', 'pop', 'jazz', 'blues', 'classical', 'folk', 'electronic', 'reggae', 'hiphop', 'metal', 'country', 'rnb'];
const INSTRUMENTS = ['guitar', 'piano', 'voice', 'drums', 'bass', 'violin', 'ukulele', 'saxophone'];
const DIFFICULTIES = ['beginner', 'intermediate', 'advanced', 'pro'];

const EMPTY_SONG = {
  title: '',
  artist: '',
  genre: 'rock',
  instruments: ['guitar'],
  difficulty: 'beginner',
  duration: '3:30',
  year: new Date().getFullYear(),
  key: 'C',
  bpm: 100,
  description: '',
  chords: '',
  lyrics: '',
  tab: '',
  chord_timeline: [],
  time_signature: '4/4',
  video_url: '',
  video_url_alt: '',
  audio_url: '',
  cover_url: '',
  is_published: true,
};

export default function Studio() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [mySongs, setMySongs] = useState([]);
  const [editing, setEditing] = useState(null); // null = list view, {} = new song, {id..} = edit
  const [saving, setSaving] = useState(false);
  const [detectingSync, setDetectingSync] = useState(false);
  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState(false);

  const isPro = user?.plan?.is_pro || user?.is_admin;

  useEffect(() => {
    (async () => {
      if (!isPro) { setLoading(false); return; }
      try {
        const { data } = await api.get('/studio/my-songs');
        setMySongs(data);
      } catch (e) {
        console.error(e);
      } finally { setLoading(false); }
    })();
  }, [isPro]);

  // === AUTO-SAVE LOCAL (localStorage) — MUST be called every render, so before any early return ===
  const LS_KEY = user ? `maestro:studio:draft:${user.user_id || user.email}` : null;

  useEffect(() => {
    if (!LS_KEY || !isPro || editing) return;
    try {
      const raw = localStorage.getItem(LS_KEY);
      if (!raw) return;
      const draft = JSON.parse(raw);
      if (draft && (draft.title || draft.chords || (draft.chord_timeline || []).length > 0)) {
        setTimeout(() => {
          if (window.confirm('Un brouillon local non enregistré a été trouvé. Le restaurer ?')) {
            // Rehydrate timeline rows with stable _uid so React reconciliation
            // stays correct across add/remove operations.
            const hydrated = { ...EMPTY_SONG, ...draft };
            if (Array.isArray(hydrated.chord_timeline)) {
              hydrated.chord_timeline = hydrated.chord_timeline.map(r => (
                r && r._uid ? r : { _uid: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`, ...r }
              ));
            }
            setEditing(hydrated);
          } else {
            localStorage.removeItem(LS_KEY);
          }
        }, 100);
      }
    } catch (err) { console.debug('Draft restore failed', err); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [LS_KEY, isPro]);

  useEffect(() => {
    if (!LS_KEY) return;
    if (!editing) return;
    const t = setTimeout(() => {
      try { localStorage.setItem(LS_KEY, JSON.stringify(editing)); }
      catch (err) { console.debug('Draft autosave failed', err); }
    }, 800);
    return () => clearTimeout(t);
  }, [editing, LS_KEY]);

  if (loading) return <Layout><div className="p-10 text-stone-400">Chargement...</div></Layout>;

  if (!isPro) {
    return (
      <Layout>
        <div className="p-6 md:p-10 max-w-3xl">
          <div className="mb-8 rise">
            <div className="overline text-orange-400">Studio Créateur</div>
            <h1 className="font-display text-5xl md:text-6xl tracking-tight">Publie tes compositions</h1>
          </div>
          <div className="card-stone p-10 text-center border border-orange-800/60 rise">
            <Crown className="mx-auto text-orange-400 mb-4" size={56} />
            <div className="font-display text-3xl mb-3">Réservé aux membres Pro</div>
            <p className="text-stone-400 max-w-lg mx-auto mb-8">
              Le Studio te permet de publier tes propres chansons originales sur Maestro
              avec diagrammes d'accords interactifs, tablatures, Play-Along synchronisé
              et paroles complètes. Ta musique, à ton nom, apprise par la communauté.
            </p>
            <ul className="text-left max-w-md mx-auto text-stone-300 text-sm space-y-2 mb-8">
              <li className="flex gap-2"><Sparkles size={16} className="text-orange-400 shrink-0 mt-0.5" /> Éditeur de chansons complet (accords, tablatures, paroles)</li>
              <li className="flex gap-2"><Sparkles size={16} className="text-orange-400 shrink-0 mt-0.5" /> Timeline Play-Along synchronisée au BPM</li>
              <li className="flex gap-2"><Sparkles size={16} className="text-orange-400 shrink-0 mt-0.5" /> Diagrammes d'accords SVG générés automatiquement</li>
              <li className="flex gap-2"><Sparkles size={16} className="text-orange-400 shrink-0 mt-0.5" /> Ton catalogue dans la bibliothèque publique</li>
            </ul>
            <Link to="/pro" data-testid="studio-pro-cta" className="btn-primary inline-flex items-center gap-2">
              <Crown size={16} /> Passer Pro pour publier
            </Link>
          </div>
        </div>
      </Layout>
    );
  }

  const refresh = async () => {
    const { data } = await api.get('/studio/my-songs');
    setMySongs(data);
  };

  const startNew = () => {
    setEditing({ ...EMPTY_SONG, artist: user?.name || '' });
    setPreview(false);
  };
  const hydrateTimeline = (song) => {
    if (!song || !Array.isArray(song.chord_timeline)) return song;
    return {
      ...song,
      chord_timeline: song.chord_timeline.map(r => (r && r._uid ? r : { _uid: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`, ...r })),
    };
  };

  const startEdit = (song) => {
    setEditing(hydrateTimeline({ ...EMPTY_SONG, ...song }));
    setPreview(false);
  };
  const cancelEdit = () => {
    if (editing && !editing.id && (editing.title || editing.chords || editing.lyrics || (editing.chord_timeline||[]).length)) {
      if (!window.confirm('Ton brouillon local sera conservé et te sera proposé à ta prochaine ouverture. Fermer maintenant ?')) return;
    }
    setEditing(null); setPreview(false);
  };

  const handleSave = async (publish) => {
    if (!editing.title || !editing.title.trim()) { toast.error('Le titre est obligatoire'); return; }
    setSaving(true);
    try {
      // Strip the client-side stable `_uid` from timeline rows before persisting.
      const cleanTimeline = (editing.chord_timeline || []).map(({ _uid, ...rest }) => rest);
      const payload = {
        ...editing,
        chord_timeline: cleanTimeline,
        is_published: publish,
        year: Number(editing.year) || null,
        bpm: Number(editing.bpm) || null,
      };
      // Clean empty strings for optional fields
      ['video_url','video_url_alt','audio_url','cover_url','description','chords','lyrics','tab','key'].forEach(k=>{
        if (payload[k] === '') payload[k] = null;
      });
      let saved;
      if (editing.id) {
        const { data } = await api.put(`/songs/${editing.id}`, payload);
        saved = data;
      } else {
        const { data } = await api.post('/songs', payload);
        saved = data;
      }
      toast.success(publish ? 'Chanson publiée !' : 'Brouillon enregistré');
      // Successful save = clear the local draft
      if (LS_KEY) {
        try { localStorage.removeItem(LS_KEY); }
        catch (err) { console.debug('localStorage clear failed', err); }
      }
      await refresh();
      setEditing(hydrateTimeline({ ...EMPTY_SONG, ...saved }));
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.detail || 'Impossible d\'enregistrer');
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette chanson définitivement ?')) return;
    try {
      await api.delete(`/songs/${id}`);
      toast.success('Supprimée');
      await refresh();
    } catch { toast.error('Impossible de supprimer'); }
  };

  // Generate a stable identifier for each timeline row so React's reconciliation
  // stays correct when a row in the middle is added/removed. Stripped on save.
  const newRow = (partial) => ({ _uid: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`, ...partial });

  const addTimelineRow = () => setEditing(e => ({ ...e, chord_timeline: [...(e.chord_timeline || []), newRow({ chord: 'C', beats: 4 })] }));
  const updateTimelineRow = (i, field, value) => setEditing(e => {
    const t = [...(e.chord_timeline || [])];
    t[i] = { ...t[i], [field]: field === 'beats' ? (Number(value) || 1) : value };
    return { ...e, chord_timeline: t };
  });
  const removeTimelineRow = (i) => setEditing(e => ({ ...e, chord_timeline: (e.chord_timeline || []).filter((_, idx) => idx !== i) }));

  // === MEASURE-BASED TIMELINE HELPERS ===
  // Parse time_signature "4/4" → numerator 4 (beats per measure)
  const beatsPerMeasure = (() => {
    const ts = (editing?.time_signature || '4/4').split('/');
    const n = Number(ts[0]);
    return Number.isFinite(n) && n > 0 ? n : 4;
  })();

  // Group flat chord_timeline into measures. An entry may span multiple measures
  // if its beats > remaining beats in the current measure — in that case we
  // still keep it in ONE measure visually and let the UI flag overflow.
  const measures = (() => {
    const flat = editing?.chord_timeline || [];
    if (flat.length === 0) return [];
    const result = [];
    let current = { entries: [], startIndex: 0, totalBeats: 0 };
    for (let i = 0; i < flat.length; i++) {
      const row = flat[i];
      const beats = Number(row.beats) || 0;
      if (current.entries.length === 0) current.startIndex = i;
      current.entries.push(row);
      current.totalBeats += beats;
      if (current.totalBeats >= beatsPerMeasure) {
        result.push(current);
        current = { entries: [], startIndex: i + 1, totalBeats: 0 };
      }
    }
    if (current.entries.length > 0) result.push(current);
    return result;
  })();

  const addMeasure = () => setEditing(e => ({
    ...e,
    chord_timeline: [...(e.chord_timeline || []), newRow({ chord: 'C', beats: beatsPerMeasure })],
  }));

  const addChordToMeasure = (measureIdx) => setEditing(e => {
    const flat = [...(e.chord_timeline || [])];
    // Find insertion point: right after the last entry of the target measure
    const m = measures[measureIdx];
    if (!m) return e;
    const insertAt = m.startIndex + m.entries.length;
    flat.splice(insertAt, 0, newRow({ chord: 'C', beats: 1 }));
    return { ...e, chord_timeline: flat };
  });

  const removeMeasure = (measureIdx) => setEditing(e => {
    const m = measures[measureIdx];
    if (!m) return e;
    const flat = [...(e.chord_timeline || [])];
    flat.splice(m.startIndex, m.entries.length);
    return { ...e, chord_timeline: flat };
  });

  const toggleInstrument = (inst) => setEditing(e => {
    const list = e.instruments || [];
    return { ...e, instruments: list.includes(inst) ? list.filter(x => x !== inst) : [...list, inst] };
  });

  // ==== LIST VIEW ====
  if (!editing) {
    return (
      <Layout>
        <div className="p-6 md:p-10 max-w-6xl">
          <div className="flex items-start justify-between mb-8 rise gap-4 flex-wrap">
            <div>
              <div className="overline text-orange-400">Studio Créateur · Pro</div>
              <h1 className="font-display text-5xl md:text-6xl tracking-tight">Mes créations</h1>
              <p className="text-stone-400 mt-2 max-w-xl">
                Publie tes chansons originales avec diagrammes, tablatures et Play-Along.
              </p>
            </div>
            <button data-testid="studio-new-btn" onClick={startNew} className="btn-primary flex items-center gap-2">
              <PlusCircle size={18} /> Nouvelle chanson
            </button>
          </div>

          {mySongs.length === 0 ? (
            <div className="card-stone p-10 text-center rise">
              <Music2 className="mx-auto text-orange-400 mb-4" size={40} />
              <div className="font-display text-2xl mb-2">Aucune création pour le moment</div>
              <p className="text-stone-400 mb-6 max-w-md mx-auto">Crée ta première chanson : ajoute paroles, accords, tablature et une progression pour le Play-Along.</p>
              <button data-testid="studio-empty-cta" onClick={startNew} className="btn-primary inline-flex items-center gap-2">
                <PlusCircle size={16} /> Commencer
              </button>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-4 rise">
              {mySongs.map(s => (
                <div key={s.id} data-testid={`my-song-${s.id}`} className="card-stone p-5 flex gap-4">
                  <div className="w-20 h-20 rounded-xl bg-stone-800 overflow-hidden shrink-0">
                    {s.cover_url && <img src={s.cover_url} alt={s.title} className="w-full h-full object-cover" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="font-display text-xl truncate">{s.title}</div>
                        <div className="text-stone-400 text-sm truncate">{s.artist} · {s.genre} · {s.difficulty}</div>
                      </div>
                      <span className={`text-xs px-2 py-0.5 rounded-full shrink-0 ${s.is_published === false ? 'bg-stone-800 text-stone-400 border border-stone-700' : 'bg-orange-950/60 text-orange-300 border border-orange-800/50'}`}>
                        {s.is_published === false ? 'Brouillon' : 'Publiée'}
                      </span>
                    </div>
                    <div className="mt-3 flex gap-2 flex-wrap">
                      <Link to={`/song/${s.id}`} className="btn-ghost text-xs flex items-center gap-1"><Eye size={13} /> Voir</Link>
                      <button data-testid={`edit-${s.id}`} onClick={() => startEdit(s)} className="btn-ghost text-xs flex items-center gap-1"><FileEdit size={13} /> Modifier</button>
                      <button data-testid={`delete-${s.id}`} onClick={() => handleDelete(s.id)} className="btn-ghost text-xs flex items-center gap-1 text-red-400"><Trash2 size={13} /> Supprimer</button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </Layout>
    );
  }

  // ==== EDITOR VIEW ====
  return (
    <Layout>
      <div className="p-6 md:p-10 max-w-6xl">
        <div className="flex items-center justify-between mb-6 rise flex-wrap gap-3">
          <div>
            <div className="overline text-orange-400">Studio · {editing.id ? 'Édition' : 'Nouvelle chanson'}</div>
            <h1 className="font-display text-4xl md:text-5xl tracking-tight">
              {editing.title || 'Sans titre'}
            </h1>
          </div>
          <div className="flex gap-2 flex-wrap items-center">
            <span className="hidden md:inline-flex items-center gap-1.5 text-xs text-emerald-400/80 mr-2" data-testid="studio-autosave-indicator" title="Auto-sauvegarde locale toutes les secondes — tu ne perdras rien même si tu fermes la page">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
              Brouillon auto-sauvegardé
            </span>
            <button onClick={cancelEdit} className="btn-ghost">Retour</button>
            <button data-testid="studio-preview-toggle" onClick={() => setPreview(p => !p)} className="btn-ghost flex items-center gap-2">
              <Eye size={16} /> {preview ? 'Éditer' : 'Aperçu'}
            </button>
            <button data-testid="studio-save-draft" onClick={() => handleSave(false)} disabled={saving} className="btn-ghost flex items-center gap-2">
              <Save size={16} /> Brouillon
            </button>
            <button data-testid="studio-publish" onClick={() => handleSave(true)} disabled={saving} className="btn-primary flex items-center gap-2">
              <ArrowRight size={16} /> Publier
            </button>
          </div>
        </div>

        {preview ? (
          <PreviewCard song={editing} />
        ) : (
          <div className="grid lg:grid-cols-3 gap-6 rise">
            {/* Left column - Meta */}
            <div className="lg:col-span-1 space-y-4">
              <Field label="Titre *">
                <input data-testid="studio-title" value={editing.title} onChange={e => setEditing({ ...editing, title: e.target.value })} className="input-stone" />
              </Field>
              <Field label="Artiste (sera ton nom)">
                <input value={editing.artist} disabled className="input-stone opacity-60" />
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Genre">
                  <select data-testid="studio-genre" value={editing.genre} onChange={e => setEditing({ ...editing, genre: e.target.value })} className="input-stone">
                    {GENRES.map(g => <option key={g} value={g}>{g}</option>)}
                  </select>
                </Field>
                <Field label="Difficulté">
                  <select data-testid="studio-difficulty" value={editing.difficulty} onChange={e => setEditing({ ...editing, difficulty: e.target.value })} className="input-stone">
                    {DIFFICULTIES.map(d => <option key={d} value={d}>{d}</option>)}
                  </select>
                  {(() => {
                    const est = estimateDifficulty(editing.chords, editing.bpm);
                    if (!est.level || est.level === editing.difficulty) return null;
                    return (
                      <div className="mt-1.5 flex items-center gap-2 text-[11px]" data-testid="studio-diff-suggestion">
                        <span className="text-stone-500">Suggestion auto :</span>
                        <button
                          type="button"
                          onClick={() => setEditing({ ...editing, difficulty: est.level })}
                          data-testid="studio-diff-apply"
                          title={est.reasons.join(', ')}
                          className="px-2 py-0.5 rounded-full bg-orange-950/60 text-orange-300 border border-orange-800/50 hover:bg-orange-900/60"
                        >
                          {DIFFICULTY_LABEL_FR[est.level]} · appliquer
                        </button>
                      </div>
                    );
                  })()}
                </Field>
              </div>
              <Field label="Instruments">
                <div className="flex flex-wrap gap-2">
                  {INSTRUMENTS.map(i => (
                    <button
                      key={i}
                      type="button"
                      onClick={() => toggleInstrument(i)}
                      className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                        (editing.instruments || []).includes(i)
                          ? 'bg-orange-700/30 border-orange-700 text-orange-200'
                          : 'bg-stone-900 border-stone-700 text-stone-400 hover:border-stone-600'
                      }`}
                    >{i}</button>
                  ))}
                </div>
              </Field>
              <div className="grid grid-cols-3 gap-3">
                <Field label="Tonalité">
                  <input value={editing.key || ''} onChange={e => setEditing({ ...editing, key: e.target.value })} className="input-stone" placeholder="C" />
                </Field>
                <Field label="BPM">
                  <input type="number" value={editing.bpm || ''} onChange={e => setEditing({ ...editing, bpm: e.target.value })} className="input-stone" placeholder="100" />
                </Field>
                <Field label="Durée">
                  <input value={editing.duration || ''} onChange={e => setEditing({ ...editing, duration: e.target.value })} className="input-stone" placeholder="3:30" />
                </Field>
              </div>
              <Field label="Cover URL (image)">
                <input value={editing.cover_url || ''} onChange={e => setEditing({ ...editing, cover_url: e.target.value })} className="input-stone" placeholder="https://..." />
              </Field>
              <Field label="Vidéo tutoriel (YouTube embed)">
                <input value={editing.video_url || ''} onChange={e => setEditing({ ...editing, video_url: e.target.value })} className="input-stone" placeholder="https://www.youtube.com/embed/..." />
              </Field>
              <Field label="Vidéo alt. (karaoké / live / cover embarquable)" hint="Utilisée en priorité pour le Play-Along synchronisé. À renseigner uniquement si la vidéo officielle bloque l'intégration YouTube (Wonderwall, Smoke on the Water…).">
                <input value={editing.video_url_alt || ''} onChange={e => setEditing({ ...editing, video_url_alt: e.target.value })} className="input-stone" placeholder="https://www.youtube.com/embed/... (karaoké/instrumental)" data-testid="edit-video-url-alt" />
              </Field>
              <Field label="Décalage sync par défaut (secondes)" hint="Si l'intro du clip est plus longue que t=0 (logo, silence), indique combien de secondes avant que la musique démarre. Le bouton auto-détecte via l'audio si tu l'as uploadé.">
                <div className="flex gap-2 items-center">
                  <input
                    type="number"
                    step="0.5"
                    value={editing.sync_offset ?? 0}
                    onChange={e => setEditing({ ...editing, sync_offset: parseFloat(e.target.value) || 0 })}
                    className="input-stone flex-1"
                    placeholder="0.0"
                    data-testid="edit-sync-offset"
                  />
                  <button
                    type="button"
                    data-testid="detect-sync-offset-btn"
                    disabled={!editing.audio_url || detectingSync}
                    onClick={async () => {
                      if (!editing.audio_url) return;
                      setDetectingSync(true);
                      try {
                        const { data } = await api.post('/analyze-audio/detect-sync-offset', { audio_url: editing.audio_url });
                        setEditing(cur => ({ ...cur, sync_offset: data.sync_offset }));
                        toast.success(`Début musique détecté à ${data.sync_offset}s`);
                      } catch (err) {
                        toast.error("Impossible de détecter (audio non téléchargeable ?)");
                        console.debug(err);
                      } finally {
                        setDetectingSync(false);
                      }
                    }}
                    className="btn-ghost text-xs px-3 py-2 whitespace-nowrap"
                    title={editing.audio_url ? "Analyser l'audio et proposer un décalage automatique" : "Ajoute un audio pour activer la détection auto"}
                  >
                    {detectingSync ? '…' : '🎯 Auto-détecter'}
                  </button>
                </div>
              </Field>
              <Field label="Audio original de la chanson" hint="MP3, WAV, OGG, M4A · Max 20 MB. Ton élève pourra écouter ta chanson directement sur Maestro.">
                <AudioUploader
                  value={editing.audio_url}
                  onChange={(url) => setEditing({ ...editing, audio_url: url })}
                />
              </Field>
              <Field label="Description">
                <textarea rows={3} value={editing.description || ''} onChange={e => setEditing({ ...editing, description: e.target.value })} className="input-stone" placeholder="Petite histoire ou astuce sur ta chanson..." />
              </Field>
            </div>

            {/* Right column - Content */}
            <div className="lg:col-span-2 space-y-6">
              <Field label="Accords + Paroles (les accords sur leur ligne au-dessus des paroles)" hint="Les accords reconnus (C, Am, F#m7...) afficheront automatiquement leur diagramme.">
                <textarea
                  data-testid="studio-chords"
                  rows={10}
                  value={editing.chords || ''}
                  onChange={e => setEditing({ ...editing, chords: e.target.value })}
                  className="input-stone font-mono text-sm"
                  placeholder={"C           G           Am        F\nHere are the lyrics of my song...\nC           G           F         C\nMore lyrics here..."}
                />
              </Field>

              <Field label="Tablature (police monospace, 6 lignes E-A-D-G-B-e)">
                <textarea
                  data-testid="studio-tab"
                  rows={7}
                  value={editing.tab || ''}
                  onChange={e => setEditing({ ...editing, tab: e.target.value })}
                  className="input-stone font-mono text-xs"
                  placeholder={"e|-----------------------------|\nB|-----------------------------|\nG|-----------------------------|\nD|-----------------------------|\nA|-----------------------------|\nE|-----------------------------|"}
                />
              </Field>

              <Field label="Paroles complètes">
                <textarea
                  data-testid="studio-lyrics"
                  rows={8}
                  value={editing.lyrics || ''}
                  onChange={e => setEditing({ ...editing, lyrics: e.target.value })}
                  className="input-stone text-sm"
                  placeholder={"[Couplet 1]\nÉcris tes paroles ici...\n\n[Refrain]\n..."}
                />
              </Field>

              <div>
                <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                  <div className="text-sm text-stone-300 font-medium">Timeline Play-Along · Par mesures</div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <label className="text-xs text-stone-400 flex items-center gap-2">
                      Signature :
                      <select
                        data-testid="studio-time-signature"
                        value={editing.time_signature || '4/4'}
                        onChange={e => setEditing({ ...editing, time_signature: e.target.value })}
                        className="input-stone !w-auto !py-1 !px-2 text-xs"
                      >
                        {['4/4','3/4','2/4','6/8','12/8','5/4','7/8'].map(t => <option key={t} value={t}>{t}</option>)}
                      </select>
                    </label>
                    <button
                      type="button"
                      onClick={() => {
                        const { timeline, measures, warnings } = parseChordText(editing.chords || '', beatsPerMeasure);
                        if (warnings.length > 0 && timeline.length === 0) {
                          toast.error(warnings[0]);
                          return;
                        }
                        if ((editing.chord_timeline || []).length > 0) {
                          // eslint-disable-next-line no-alert
                          if (!window.confirm(`Cela va REMPLACER ta timeline actuelle par ${measures} mesure(s) parsée(s). Continuer ?`)) return;
                        }
                        const withUids = timeline.map(row => newRow(row));
                        setEditing(e => ({ ...e, chord_timeline: withUids }));
                        toast.success(`Timeline générée : ${measures} mesure(s), ${timeline.length} accord(s).`);
                      }}
                      data-testid="studio-import-chord-text"
                      className="btn-ghost text-xs flex items-center gap-1"
                      title="Analyser le champ 'Accords + Paroles' et remplir la timeline automatiquement"
                    >
                      <Wand2 size={13} /> Importer depuis Accords
                    </button>
                    <AudioAnalyzeButton
                      audioUrl={editing.audio_url}
                      songId={editing.id}
                      timeSignature={editing.time_signature || '4/4'}
                      isPro={isPro}
                      onApply={({ chord_timeline, bpm, time_signature }) => {
                        setEditing(e => ({
                          ...e,
                          chord_timeline: chord_timeline.map(row => newRow(row)),
                          bpm: bpm || e.bpm,
                          time_signature: time_signature || e.time_signature,
                        }));
                      }}
                    />
                    <button type="button" onClick={addMeasure} data-testid="studio-add-measure" className="btn-primary text-xs flex items-center gap-1">
                      <PlusCircle size={13} /> Nouvelle mesure
                    </button>
                  </div>
                </div>
                <div className="text-xs text-stone-500 mb-3">
                  Chaque mesure doit totaliser <b className="text-stone-300">{beatsPerMeasure}</b> temps.
                  Tu peux mettre plusieurs accords par mesure (ex: 2 accords de 2 temps chacun).
                </div>
                <div className="card-stone p-4 space-y-3">
                  {(editing.chord_timeline || []).length === 0 && (
                    <div className="text-stone-500 text-xs text-center py-4">
                      Aucune mesure. Clique <b>“Nouvelle mesure”</b> pour commencer ta progression.
                    </div>
                  )}
                  {measures.map((measure, mi) => {
                    const measureBeats = measure.entries.reduce((s, e) => s + (Number(e.beats) || 0), 0);
                    const complete = measureBeats === beatsPerMeasure;
                    const over = measureBeats > beatsPerMeasure;
                    // Detect section change from previous measure to render a header
                    const thisSection = measure.entries[0]?.section || null;
                    const prevSection = mi > 0 ? (measures[mi - 1].entries[0]?.section || null) : '__START__';
                    const showSectionHeader = thisSection && thisSection !== prevSection;
                    return (
                      <div key={mi}>
                        {showSectionHeader && (
                          <SectionHeader
                            label={thisSection}
                            onRename={(newLabel) => {
                              if (!newLabel || newLabel === thisSection) return;
                              setEditing(e => ({
                                ...e,
                                chord_timeline: (e.chord_timeline || []).map(r =>
                                  r.section === thisSection ? { ...r, section: newLabel } : r
                                ),
                              }));
                              toast.success(`Section renommée : ${newLabel}`);
                            }}
                            testId={`section-header-${mi}`}
                          />
                        )}
                      <div
                        data-testid={`measure-${mi}`}
                        className={`rounded-xl p-3 border ${
                          over ? 'border-red-800/60 bg-red-950/10'
                          : complete ? 'border-emerald-800/40 bg-emerald-950/10'
                          : 'border-amber-800/40 bg-amber-950/10'
                        }`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <div className="text-xs font-display text-stone-200 flex items-center gap-2">
                            Mesure {mi + 1}
                            <span className={`text-[10px] px-1.5 py-0.5 rounded-full border ${
                              over ? 'text-red-300 border-red-800/60'
                              : complete ? 'text-emerald-300 border-emerald-800/60'
                              : 'text-amber-300 border-amber-800/60'
                            }`}>
                              {measureBeats}/{beatsPerMeasure} temps {over ? '⚠️' : complete ? '✅' : ''}
                            </span>
                          </div>
                          <div className="flex gap-1">
                            <button
                              type="button"
                              onClick={() => addChordToMeasure(mi)}
                              data-testid={`measure-${mi}-add-chord`}
                              className="btn-ghost text-xs flex items-center gap-1 !py-1 !px-2"
                            ><PlusCircle size={12} /> Accord</button>
                            <button
                              type="button"
                              onClick={() => removeMeasure(mi)}
                              data-testid={`measure-${mi}-remove`}
                              className="btn-ghost text-red-400 !py-1 !px-2"
                              title="Supprimer cette mesure"
                            ><Trash2 size={12} /></button>
                          </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                          {measure.entries.map((row, ei) => {
                            const globalIdx = measure.startIndex + ei;
                            return (
                              <div key={row._uid || `row-${globalIdx}`} className="flex gap-2 items-center bg-stone-900/60 rounded-lg p-2">
                                <input
                                  value={row.chord}
                                  onChange={e => updateTimelineRow(globalIdx, 'chord', e.target.value)}
                                  className="input-stone flex-1 min-w-0 text-sm"
                                  placeholder="Accord (ex: C, Am, F#m7)"
                                  data-testid={`timeline-chord-${globalIdx}`}
                                />
                                <input
                                  type="number"
                                  min="1"
                                  max={beatsPerMeasure * 2}
                                  value={row.beats}
                                  onChange={e => updateTimelineRow(globalIdx, 'beats', e.target.value)}
                                  className="input-stone !w-16 shrink-0 text-sm text-center"
                                  placeholder="Temps"
                                  data-testid={`timeline-beats-${globalIdx}`}
                                  title="Nombre de temps"
                                />
                                <button
                                  type="button"
                                  onClick={() => removeTimelineRow(globalIdx)}
                                  className="btn-ghost text-red-400 !py-1 !px-2 shrink-0"
                                  data-testid={`timeline-remove-${globalIdx}`}
                                ><Trash2 size={12} /></button>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                      </div>
                    );
                  })}
                </div>
                {(editing.chord_timeline || []).length > 0 && (
                  <BackingTrackPreview
                    timeline={editing.chord_timeline}
                    bpm={Number(editing.bpm) || 90}
                    beatsPerMeasure={beatsPerMeasure}
                  />
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}

function Field({ label, hint, children }) {
  return (
    <label className="block">
      <div className="text-sm text-stone-300 font-medium mb-1.5">{label}</div>
      {children}
      {hint && <div className="text-xs text-stone-500 mt-1">{hint}</div>}
    </label>
  );
}

function PreviewCard({ song }) {
  return (
    <div className="rise space-y-8">
      <div className="grid md:grid-cols-12 gap-6">
        <div className="md:col-span-4">
          <div className="aspect-square rounded-2xl overflow-hidden border border-stone-800 bg-stone-900">
            {song.cover_url && <img src={song.cover_url} alt="" className="w-full h-full object-cover" />}
          </div>
        </div>
        <div className="md:col-span-8">
          <div className="overline text-orange-400 mb-2">{song.genre} · {song.difficulty} · {(song.instruments || []).join(', ')}</div>
          <h2 className="font-display text-4xl">{song.title || 'Sans titre'}</h2>
          <div className="text-stone-300 mt-1">{song.artist}</div>
          {song.description && <p className="text-stone-400 mt-3 max-w-xl">{song.description}</p>}
          <div className="flex flex-wrap gap-2 text-xs text-stone-400 mt-4">
            {song.key && <span className="px-2 py-1 rounded bg-stone-800">Tonalité {song.key}</span>}
            {song.bpm && <span className="px-2 py-1 rounded bg-stone-800">{song.bpm} BPM</span>}
            {song.duration && <span className="px-2 py-1 rounded bg-stone-800">{song.duration}</span>}
          </div>
        </div>
      </div>
      {song.chords && (
        <div>
          <div className="overline text-stone-400 mb-3">Accords</div>
          <ChordChart text={song.chords} />
        </div>
      )}
      {song.tab && (
        <div>
          <div className="overline text-stone-400 mb-3">Tablature</div>
          <pre className="card-stone p-6 font-mono text-xs text-stone-200 whitespace-pre-wrap overflow-x-auto">{song.tab}</pre>
        </div>
      )}
      {song.lyrics && (
        <div>
          <div className="overline text-stone-400 mb-3">Paroles</div>
          <pre className="card-stone p-6 font-sans text-sm text-stone-200 whitespace-pre-wrap overflow-x-auto">{song.lyrics}</pre>
        </div>
      )}
    </div>
  );
}

function AudioUploader({ value, onChange }) {
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const handleFile = async (file) => {
    if (!file) return;
    if (file.size > 20 * 1024 * 1024) {
      toast.error('Fichier trop lourd (max 20 MB)');
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await api.post('/uploads/audio', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const backend = process.env.REACT_APP_BACKEND_URL;
      const fullUrl = `${backend}${data.url}`;
      onChange(fullUrl);
      toast.success('Audio uploadé ! Tes élèves pourront l\'écouter.');
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.detail || 'Upload impossible');
    } finally { setUploading(false); }
  };

  if (value) {
    return (
      <div className="card-stone p-3 flex items-center gap-3">
        <audio controls src={value} className="flex-1 h-10" data-testid="studio-audio-preview" />
        <button
          type="button"
          onClick={() => onChange('')}
          className="btn-ghost !py-1.5 !px-2 text-red-400"
          data-testid="studio-audio-remove"
          title="Retirer l'audio"
        >
          <X size={14} />
        </button>
      </div>
    );
  }

  return (
    <div>
      <input
        ref={inputRef}
        type="file"
        accept="audio/*"
        className="hidden"
        onChange={e => handleFile(e.target.files?.[0])}
        data-testid="studio-audio-input"
      />
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={uploading}
        className="w-full card-stone p-6 border-dashed border-stone-700 hover:border-orange-800/60 transition-colors flex flex-col items-center justify-center gap-2 text-stone-400 hover:text-stone-200"
        data-testid="studio-audio-upload-btn"
      >
        {uploading ? (
          <>
            <Loader2 className="animate-spin" size={20} />
            <span className="text-sm">Upload en cours…</span>
          </>
        ) : (
          <>
            <Upload size={20} />
            <span className="text-sm">Cliquer pour uploader un fichier audio</span>
            <span className="text-xs text-stone-500">MP3 · WAV · OGG · M4A (max 20 MB)</span>
          </>
        )}
      </button>
    </div>
  );
}


function SectionHeader({ label, onRename, testId }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(label);
  useEffect(() => { setDraft(label); }, [label]);

  const submit = () => {
    setEditing(false);
    const clean = (draft || '').trim();
    if (clean && clean !== label) onRename(clean);
  };

  return (
    <div
      data-testid={testId}
      className="my-4 flex items-center gap-3 group"
    >
      <div className="flex-1 h-px bg-gradient-to-r from-transparent via-orange-800/60 to-transparent" />
      {editing ? (
        <input
          value={draft}
          autoFocus
          onChange={e => setDraft(e.target.value)}
          onBlur={submit}
          onKeyDown={e => { if (e.key === 'Enter') submit(); if (e.key === 'Escape') { setDraft(label); setEditing(false); } }}
          data-testid={`${testId}-input`}
          className="text-sm font-display bg-stone-900 border border-orange-800 rounded-md px-3 py-1 text-orange-200 w-40 text-center"
        />
      ) : (
        <button
          type="button"
          onClick={() => setEditing(true)}
          data-testid={`${testId}-btn`}
          title="Cliquer pour renommer la section"
          className="text-xs font-display uppercase tracking-widest px-3 py-1 rounded-full bg-orange-950/50 text-orange-300 border border-orange-800/60 hover:bg-orange-900/60 hover:text-orange-200 transition-colors"
        >
          {label}
          <span className="opacity-0 group-hover:opacity-100 ml-1.5 text-[9px]">✎</span>
        </button>
      )}
      <div className="flex-1 h-px bg-gradient-to-r from-orange-800/60 via-orange-800/60 to-transparent" />
    </div>
  );
}

